/**
 * NikkiDailyRead — the visible surface of Nikki's behaviour (COM-B) engine.
 *
 * Same component renders on the dashboard (variant="dashboard") and inline in
 * chat (variant="chat"). Both are fed the SAME payload from
 * GET /api/wellness/daily-read (or the chat bootstrap card), so what Nikki
 * "notices" is identical wherever the user meets her.
 *
 * Reads in four beats, in order: Pillars (where you stand) -> Why (the body/mind
 * cause) -> Behaviour (follow-through, a SEPARATE truth) -> Action (today's move).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Activity, Flower2, TrendingUp, TrendingDown, Trophy, Check } from 'lucide-react';

const MODALITY = {
  strength_upper:    { label: 'Upper Strength', color: '#CCFF00' },
  strength_lower:    { label: 'Lower Strength', color: '#CCFF00' },
  strength_full:     { label: 'Full Body',      color: '#CCFF00' },
  hybrid:            { label: 'Hybrid',         color: '#FBBF24' },
  mobility:          { label: 'Mobility',       color: '#22D3EE' },
  yoga:              { label: 'Yoga',           color: '#A855F7' },
  breathwork:        { label: 'Breathwork',     color: '#60A5FA' },
  injury_management: { label: 'Injury Care',    color: '#F87171' },
  walk:              { label: 'Walk · Run',     color: '#34D399' },
};
const meta = (m) => MODALITY[m] || { label: m || 'Session', color: '#CCFF00' };

const NikkiDailyRead = ({ data, variant = 'dashboard' }) => {
  const navigate = useNavigate();
  if (!data) return null;

  const { noticing, read, behaviour, behaviour_tone, move, why_chips = [], action, cycle_note, empty, checked_in_today } = data;
  const m = move ? meta(move.modality) : null;

  const wrapClass =
    variant === 'chat'
      ? 'rounded-sm border border-primary/25 bg-gradient-to-br from-zinc-900/70 to-zinc-900/20 p-4 space-y-3'
      : 'hpme-card p-6 space-y-4 overflow-hidden';

  return (
    <div className={wrapClass} data-testid="daily-read-card">
      {variant === 'dashboard' && <div className="hpme-card-corner" />}

      {/* Header — Nikki's voice */}
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 border border-primary/40">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
        </span>
        <span className="hpme-label" style={{ letterSpacing: '0.18em' }}>Nikki's Daily Read</span>
      </div>

      {/* What I'm noticing */}
      <p
        className={`font-secondary font-bold leading-snug text-white ${variant === 'chat' ? 'text-base' : 'text-lg md:text-xl'}`}
        data-testid="daily-read-noticing"
      >
        {noticing}
      </p>

      {/* WHY beat — the body/mind cause */}
      {read && !empty && (
        <p className="text-sm text-zinc-400 leading-relaxed" data-testid="daily-read-read">
          {read}
        </p>
      )}

      {/* BEHAVIOUR beat — follow-through, a SEPARATE truth from the why.
          Green when it's a win (closed rings / overcame blocker), amber for a blocker. */}
      {behaviour && !empty && (
        <p
          className={`text-sm leading-relaxed flex items-start gap-1.5 ${behaviour_tone === 'win' ? 'text-emerald-300' : 'text-amber-200/90'}`}
          data-testid="daily-read-behaviour"
        >
          {behaviour_tone === 'win' && <Trophy className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
          <span>{behaviour}</span>
        </p>
      )}

      {/* ACTION beat — today's move, serving both the why and the behaviour */}
      {action && !empty && (
        <p className="text-sm text-zinc-300 leading-relaxed border-l-2 border-primary pl-3" data-testid="daily-read-action">
          {action}
        </p>
      )}

      {/* Today's move — flips to a "done" celebration once a session is logged today */}
      {move?.title && move.completed && (
        <div
          className="w-full rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5"
          data-testid="daily-read-move-done"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-400/15 border border-emerald-400/40 shrink-0">
              <Check className="w-3.5 h-3.5 text-emerald-300" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300/80 mb-0.5">Today's Focus · done</p>
              <p className="text-sm text-zinc-100 font-medium truncate">Nice work — that's logged.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="mt-2 inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
            data-testid="daily-read-work-on-something-else"
          >
            Want to work on something else? <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {move?.title && !move.completed && (
        <button
          type="button"
          onClick={() => navigate('/workouts')}
          className="w-full text-left rounded-sm border border-zinc-800 hover:border-primary/50 bg-zinc-900/40 px-3 py-2.5 flex items-center justify-between gap-3 transition-colors group"
          data-testid="daily-read-move"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Today's Focus</p>
            <p className="text-sm text-zinc-100 font-medium truncate">{move.title}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-secondary font-bold uppercase tracking-wider"
              style={{ color: m.color, backgroundColor: `${m.color}1A`, border: `1px solid ${m.color}33` }}
            >
              <Activity className="w-2.5 h-2.5" /> {m.label}{move.duration_min ? ` · ${move.duration_min}m` : ''}
            </span>
            <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-primary transition-colors" />
          </div>
        </button>
      )}

      {/* Why — ranked driver chips */}
      {why_chips.length > 0 && (
        <div data-testid="daily-read-why">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Why</p>
          <div className="flex flex-wrap gap-1.5">
            {why_chips.map((d, i) => {
              const positive = (d.impact || 0) >= 0;
              const color = positive ? '#34D399' : '#F87171';
              const Icon = positive ? TrendingUp : TrendingDown;
              return (
                <span
                  key={i}
                  title={d.detail || ''}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px]"
                  style={{ color, backgroundColor: `${color}1A`, border: `1px solid ${color}33` }}
                  data-testid={`daily-read-chip-${i}`}
                >
                  <Icon className="w-2.5 h-2.5" /> {d.name}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Cycle nuance (opt-in only) */}
      {cycle_note && (
        <p className="text-xs text-fuchsia-300/80 flex items-start gap-1.5" data-testid="daily-read-cycle">
          <Flower2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {cycle_note}
        </p>
      )}

      {/* Empty state CTA — invite the check-in */}
      {empty && !checked_in_today && (
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
          data-testid="daily-read-checkin-cta"
        >
          Check in with Nikki <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default NikkiDailyRead;
